

















































































import Vue, { PropType } from "vue";
import { computed, onBeforeUnmount, onMounted, ref } from "@vue/composition-api";
import GaIcon from "../../card-items/GaIcon.vue";
import { useGenAiPalette } from "../../data/design";
import { EventView } from "./event-view";
import { RowAction, can_manage_trust, trust_entities } from "./row-actions";

type Pos = { position: string; top: string; left: string; width: string; zIndex: number };

const WIDTH = [200, 210, 240];

export default Vue.extend({
  components: { GaIcon },
  props: {
    event: { type: Object as PropType<EventView>, required: true },
  },
  setup(props, { emit }) {
    const T = useGenAiPalette();
    const root = ref<HTMLElement | null>(null);
    const btn = ref<HTMLElement | null>(null);
    const open = ref(false);
    /** what is open at each depth: [main item, sub item] */
    const path = ref<string[]>([]);
    const pos = ref<Pos[]>([]);

    const ev = computed(() => props.event as EventView);

    const place = (rect: DOMRect, depth: number, below: boolean): Pos => {
      const width = WIDTH[depth];
      let left = below ? rect.left : rect.right + 4;
      if (!below && left + width > innerWidth - 8) left = rect.left - width - 4;
      left = Math.max(8, Math.min(left, innerWidth - width - 8));
      return {
        position: "fixed",
        top: (below ? rect.bottom + 4 : rect.top) + "px",
        left: left + "px",
        width: width + "px",
        zIndex: 90 + depth,
      };
    };

    const toggle = () => {
      open.value = !open.value;
      path.value = [];
      if (open.value && btn.value) {
        pos.value = [place(btn.value.getBoundingClientRect(), 0, true)];
      }
    };

    const close = () => {
      open.value = false;
      path.value = [];
    };

    const main_items = computed(() => [
      { key: "investigate", icon: "binoculars", label: "Investigate" },
      ...(can_manage_trust(ev.value)
        ? [{ key: "mitigate", icon: "wrench", label: "Mitigate" }]
        : []),
    ]);

    const sub_items = computed<Record<string, { key: string; icon: string; label: string }[]>>(() => ({
      investigate: [
        { key: "agent", icon: "bot", label: "AI Agent" },
        ...(ev.value.users.length ? [{ key: "user", icon: "user", label: "User" }] : []),
        ...(ev.value.files.length ? [{ key: "files", icon: "file-text", label: "Files" }] : []),
      ],
      mitigate: [{ key: "trust", icon: "clipboard-check", label: "Manage Trust" }],
    }));

    const targets = computed<Record<string, { key: string; label: string; action: RowAction }[]>>(() => ({
      agent: [
        {
          key: "agent",
          label: ev.value.agent.name,
          action: { type: "investigate_sender" },
        },
      ],
      user: ev.value.users.map((u, i) => ({
        key: (u.id || "") + i,
        label: u.name || u.email || "Unknown",
        action: { type: "investigate_person", id: u.id as string },
      })),
      files: ev.value.files.map((f, i) => ({
        key: f.id + i,
        label: f.name,
        action: { type: "investigate_file", id: f.id },
      })),
      trust: trust_entities(ev.value).map((d: any, i: number) => ({
        key: d.id + i,
        label: d.name || d.email || "Unknown",
        action: {
          type: "manage_trust",
          id: d.id,
          entity_type: d.type === "organization" ? "organization" : "employee",
        },
      })),
    }));

    /** hovering or clicking an item opens the next level beside it */
    const enter = (depth: number, key: string, e: MouseEvent) => {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      path.value = [...path.value.slice(0, depth), key];
      pos.value = [...pos.value.slice(0, depth + 1), place(rect, depth + 1, false)];
    };

    const layers = computed(() => {
      const out: any[] = [
        { id: "main", depth: 0, items: main_items.value, active: path.value[0], pos: pos.value[0] },
      ];
      const [main, sub] = path.value;
      if (main && sub_items.value[main]) {
        out.push({ id: main, depth: 1, items: sub_items.value[main], active: sub, pos: pos.value[1] });
      }
      if (sub && targets.value[sub]) {
        out.push({ id: sub, depth: 2, targets: targets.value[sub], pos: pos.value[2] });
      }
      return out.filter((l) => l.pos);
    });

    const pick = (action: RowAction) => {
      close();
      emit("action", action);
    };

    const on_outside = (e: MouseEvent) => {
      if (!open.value) return;
      const target = e.target as HTMLElement;
      if (root.value && root.value.contains(target)) return;
      if (target.closest && target.closest(".ga-menu")) return;
      close();
    };
    const on_key = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open.value) close();
    };
    // as gen-ai-ui does: the menu follows its button when anything scrolls;
    // the side menus close, since the items they hung from have moved
    const on_scroll = () => {
      if (!open.value || !btn.value) return;
      path.value = [];
      pos.value = [place(btn.value.getBoundingClientRect(), 0, true)];
    };
    onMounted(() => {
      document.addEventListener("mousedown", on_outside);
      document.addEventListener("keydown", on_key);
      window.addEventListener("scroll", on_scroll, true);
      window.addEventListener("resize", on_scroll);
    });
    onBeforeUnmount(() => {
      document.removeEventListener("mousedown", on_outside);
      document.removeEventListener("keydown", on_key);
      window.removeEventListener("scroll", on_scroll, true);
      window.removeEventListener("resize", on_scroll);
    });

    const box = computed(() => ({
      borderRadius: "8px",
      border: "1px solid " + T.value.ev.inputBorder,
      background: T.value.ev.bg,
      boxShadow: T.value.ev.menuShadow,
      padding: "4px 0",
    }));

    return { T, root, btn, open, toggle, layers, enter, pick, box };
  },
});
